import { motion } from "framer-motion";
import { useState } from "react";
import { TEACHING_ILLUSTRATIONS } from "../lib/constants";
import { X } from "lucide-react";

const ease = [0.76, 0, 0.24, 1];

export default function Method() {
  const [lightbox, setLightbox] = useState(null);

  return (
    <section
      id="method"
      data-testid="method-section"
      className="relative py-24 md:py-32 lg:py-40 bg-cream border-t border-ink-15"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 lg:px-16">
        {/* Header — no chapter, no eyebrow */}
        <div className="max-w-5xl mb-14 md:mb-20">
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, ease }}
            className="font-display font-light text-5xl md:text-7xl lg:text-8xl leading-[0.98] tracking-[-0.03em] text-ink"
            style={{ fontVariationSettings: '"SOFT" 60' }}
          >
            Separate plan <span className="italic text-saffron">for each aspirant.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.1, ease }}
            className="mt-8 text-lg md:text-xl text-ink/75 max-w-3xl font-light leading-relaxed"
          >
            Every student receives a study plan crafted by Sir himself — timetables, monthly targets, MCQ grids, revision cycles. Reviewed and re-tuned every fifteen days. Below are real illustrations shared with real students.
          </motion.p>
        </div>

        {/* Illustrations grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {TEACHING_ILLUSTRATIONS.map((img, idx) => (
            <motion.button
              key={img.src}
              onClick={() => setLightbox(img)}
              data-testid={`illustration-${idx}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: idx * 0.12, ease }}
              className="group text-left block w-full focus:outline-none"
              aria-label={`View ${img.title}`}
            >
              <div className="frame-mount-dark relative overflow-hidden">
                <div className="relative overflow-hidden bg-ink">
                  <motion.img
                    src={img.src}
                    alt={img.title}
                    className="w-full h-auto object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.03]"
                    style={{ aspectRatio: "3/4" }}
                    loading="lazy"
                  />
                  <div className="absolute top-3 left-3 text-[10px] tracking-[0.28em] uppercase font-semibold text-ink bg-saffron px-2 py-1">
                    Illustration {String(idx + 1).padStart(2, "0")}
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute bottom-4 right-4 text-[10px] tracking-[0.28em] uppercase text-cream opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-ink/70 px-3 py-1.5 border border-saffron/50 font-semibold">
                    Zoom →
                  </div>
                </div>
                <div className="mt-3 px-1">
                  <span className="font-display italic text-cream text-base leading-tight" style={{ fontVariationSettings: '"SOFT" 80' }}>
                    {img.title}
                  </span>
                </div>
              </div>

              <p className="mt-5 text-ink/70 text-[15px] leading-relaxed font-light">
                {img.caption}
              </p>
            </motion.button>
          ))}
        </div>

        {/* Footnote strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease }}
          className="mt-14 md:mt-16 border-t border-ink-15 pt-6 flex flex-wrap items-center justify-between gap-4"
        >
          <p className="text-[10px] tracking-[0.28em] uppercase text-ink/55 font-medium">
            <span className="text-saffron">●</span>&nbsp;&nbsp;Personalised follow-up every 15 days
          </p>
          <p className="text-[10px] tracking-[0.28em] uppercase text-ink/55 font-medium">Real student strategy · Actual timetable</p>
        </motion.div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[150] bg-ink/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
          onClick={() => setLightbox(null)}
          data-testid="illustration-lightbox"
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-6 right-6 md:top-8 md:right-8 w-12 h-12 border border-cream/40 flex items-center justify-center text-cream hover:text-saffron hover:border-saffron transition-colors"
            aria-label="Close"
            data-testid="lightbox-close"
          >
            <X size={20} />
          </button>
          <motion.img
            initial={{ scale: 0.94, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.4, ease }}
            src={lightbox.src}
            alt={lightbox.title}
            className="max-w-full max-h-full object-contain border border-saffron/40 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </motion.div>
      )}
    </section>
  );
}
